import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { CroudComponent } from '../liste-prix/croud-liste-prix/croud.component';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

interface User {
    id: number;
    name: string;
    email: string;
    entreprises: string[];
    lastConnection: string;
    isActive: boolean;
}

@Component({
    selector: 'app-userandrole',
    templateUrl: './userandrole.component.html',
    styleUrls: ['./userandrole.component.scss']
})
export class UserandroleComponent implements OnInit {
    @ViewChild('croud') croudComponent!: CroudComponent;
    @ViewChild('confirmationModal') confirmationModal!: TemplateRef<any>;

    showList = true;
    isChildRoute = false;
    users: User[] = [
        {
            id: 1,
            name: 'Richard Payne',
            email: 'Layne_Kuvalis@gmail.com',
            entreprises: ['Shopi'],
            lastConnection: '02/02/2025',
            isActive: true
        },
        {
            id: 2,
            name: 'Sarah Connor',
            email: 'sarah@example.com',
            entreprises: ['Shopi Pro'],
            lastConnection: '02/02/2025',
            isActive: true
        },
        {
            id: 3,
            name: 'John Doe',
            email: 'john.doe@company.com',
            entreprises: ['Shopi Enterprise'],
            lastConnection: '02/02/2025',
            isActive: false
        }
    ];
    filteredUsers: User[] = [];
    searchTerm: string = '';

    private modalRef!: NgbModalRef;
    currentUser: User | null = null;
    currentUserId: number | null = null;
    actionType: 'activate' | 'deactivate' = 'activate';
    isProcessingStatusChange: boolean = false;
    previousUserState: boolean | null = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private modalService: NgbModal,
        private toastr: ToastrService
    ) {
        this.router.events.subscribe(() => {
            this.isChildRoute = this.route.firstChild !== null;
        });
    }

    ngOnInit(): void {
        this.filteredUsers = [...this.users];
    }

    addRole(): void {
        this.router.navigate(['/main/parametre/user-and-role/ajouter']);
    }

    toggleUserStatus(userId: number, newStatus: boolean): void {
        if (this.isProcessingStatusChange) {
            return;
        }

        const user = this.users.find(u => u.id === userId);
        if (!user) {
            this.toastr.error('Utilisateur non trouvé', 'Erreur');
            return;
        }

        this.previousUserState = user.isActive;

        this.currentUserId = userId;
        this.actionType = newStatus ? 'activate' : 'deactivate';
        this.currentUser = { ...user };

        this.openConfirmationModal();
    }

    private openConfirmationModal(): void {
        if (!this.currentUser || this.currentUserId === null) return;

        const options: NgbModalOptions = {
            size: 'md',
            centered: true,
            backdrop: 'static',
            keyboard: false,
            windowClass: 'custom-confirmation-modal'
        };

        this.modalRef = this.modalService.open(this.confirmationModal, options);

        this.modalRef.result.then(
            (result) => {
                console.log('Modal fermé avec:', result);
            },
            (reason) => {
                console.log('Modal fermé par:', reason);
                this.cancelStatusChange();
            }
        );
    }

    closeConfirmationModal(): void {
        if (this.modalRef) {
            this.modalRef.dismiss('cancel');
        }
        this.cancelStatusChange();
    }

    private cancelStatusChange(): void {
        if (this.currentUserId !== null && this.previousUserState !== null) {
            const user = this.users.find(u => u.id === this.currentUserId!);
            if (user) {
                user.isActive = this.previousUserState;
            }
        }
        this.resetConfirmationState();
    }

    confirmStatusChange(): void {
        if (!this.currentUser || this.currentUserId === null) {
            this.closeConfirmationModal();
            return;
        }

        this.isProcessingStatusChange = true;

        const user = this.users.find(u => u.id === this.currentUserId!);
        if (user) {
            user.isActive = this.actionType === 'activate';
        }

        setTimeout(() => {
            try {
                this.saveUserStatus(this.currentUserId!, this.actionType === 'activate');

                this.modalRef.close('confirmed');

                this.showStatusNotification(this.currentUser!, this.actionType);

                this.resetConfirmationState();

            } catch (error) {
                console.error('Erreur lors de la mise à jour du statut:', error);

                this.cancelStatusChange();

                this.toastr.error('Erreur lors de la mise à jour du statut', 'Erreur');
            } finally {
                // Arrêter le traitement
                this.isProcessingStatusChange = false;
            }
        }, 1000); // 1 seconde de délai simulé
    }

    private saveUserStatus(userId: number, isActive: boolean): void {
        // Mettre à jour dans le tableau (déjà fait dans confirmStatusChange)
        const userIndex = this.users.findIndex(u => u.id === userId);
        if (userIndex !== -1) {
            this.users[userIndex].isActive = isActive;
        }

        try {
            const usersData = JSON.parse(localStorage.getItem('users') || '[]');
            const localUserIndex = usersData.findIndex((u: User) => u.id === userId);

            if (localUserIndex !== -1) {
                usersData[localUserIndex].isActive = isActive;
            } else {
                const user = this.users.find(u => u.id === userId);
                if (user) {
                    usersData.push({ ...user });
                }
            }

            localStorage.setItem('users', JSON.stringify(usersData));
        } catch (error) {
            console.warn('Impossible de sauvegarder dans localStorage:', error);
            // Ne pas throw l'erreur, juste un warning
        }
    }

    private showStatusNotification(user: User, action: 'activate' | 'deactivate'): void {
        const message = action === 'activate'
            ? `L'utilisateur "${user.name}" a été activé avec succès`
            : `L'utilisateur "${user.name}" a été désactivé avec succès`;

        const title = action === 'activate' ? 'Utilisateur Activé' : 'Utilisateur Désactivé';

        this.toastr.success(
            message,
            title,
            {
                timeOut: 3000,
                progressBar: true,
                positionClass: 'toast-bottom-right'
            }
        );
    }

    private resetConfirmationState(): void {
        this.currentUser = null;
        this.currentUserId = null;
        this.actionType = 'activate';
        this.previousUserState = null;
        this.isProcessingStatusChange = false;
    }

    editUser(): void {
        this.router.navigate([`/main/parametre/user-and-role/ajouter`]);
    }

    deleteUser(userId: number): void {
        const user = this.users.find(u => u.id === userId);
        if (user && confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.name}" ?`)) {
            this.users = this.users.filter(u => u.id !== userId);
            this.filteredUsers = this.filteredUsers.filter(u => u.id !== userId);
            this.toastr.success(`Utilisateur "${user.name}" supprimé avec succès`, 'Suppression');
        }
    }

    onSearch(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.searchTerm = target.value.toLowerCase().trim();

        if (this.searchTerm === '') {
            this.filteredUsers = [...this.users];
        } else {
            this.filteredUsers = this.users.filter(user =>
                user.name.toLowerCase().includes(this.searchTerm) ||
                user.email.toLowerCase().includes(this.searchTerm)
            );
        }
    }

    clearSearch(): void {
        this.searchTerm = '';
        const searchInput = document.querySelector('.search-bar') as HTMLInputElement;
        if (searchInput) {
            searchInput.value = '';
        }
        this.filteredUsers = [...this.users];
    }
}
